import { eLinkType } from "functions/api/enums";
import { TlfItemIndexReadDto, TlfTag } from "functions/api/tlfItem/TlfItemIndexReadDto";
import { getBasedOnRelationship } from "functions/getBasedOnRelationship";

/**
 * SimpleTable row shape for the TLF item table, including derived fields used for display/filtering.
 */
interface ITlfItemTableRow extends TlfItemIndexReadDto {
  azsolIdVersion: string;
  tlfTagValues: string[];
  parentTemplate: string;
  taVersionsAvailable: string[];
}

/**
 * Maps a TLF item into the format required by the SimpleTable, including
 * derived display and filter values that are not present on the source DTO.
 *
 * Derived fields:
 * - `azsolIdVersion`: Combined AZSOL ID and version for display and filtering.
 * - `parentTemplate`: The AZSOL ID/version of the template referenced by the
 *   item's `BasedOn` relationship.
 * - `taVersionsAvailable`: A list of AZSOL ID/version values for all
 *   related templates linked through `HasVersion` relationships at TA Level.
 *
 * @param row - The source TLF item.
 * @returns The TLF item enriched with derived table fields.
 */
export const mapTlfItemTableRow = (row: TlfItemIndexReadDto): ITlfItemTableRow => {
  const basedOnTemplate = getBasedOnRelationship(row);

  return {
    ...row,
    azsolIdVersion: `${row.azsolId}/${row.version}`,
    tlfTagValues: (row.tlfTags as TlfTag[] | undefined)?.map((tag) => tag.tagValue) ?? [],
    parentTemplate: basedOnTemplate ? `${basedOnTemplate.azsolId}/${basedOnTemplate.version}` : "",
    taVersionsAvailable: row.relationships
      .filter((r) => r.linkType === eLinkType.HasVersion)
      .map((r) => `${r.azsolId}/${r.version}`),
  };
};
